import { logger } from '@accedo/logger';
import { ModalLauncher } from '@accedo/modal';
import { Page } from '@accedo/page';
import { Nav } from '@accedo/spatial-nav';
import { VirtualKeyBoardNavId } from '@accedo/virtual-keyboard';
import { ScrollProps } from '@accedo/virtual-scroll';
import { stylesheet } from 'astroturf';
import { Fragment, FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useKeyPress, useReferredState } from '##/utils/hooks';
import { SearchResponse } from '##/services/tenApi/collections/types';
import tenApiService, { RequestRejectionError } from '##/services/tenApi';
import { focusCache } from '##/services/store/store';
import { nav } from '##/init';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import { useErrorScreen } from '##/components/Error';
import {
  EXIT_MODAL_NAV_ID,
  SEARCH_LANDING_BAR_ID,
  SIDE_MENU_NAV_ID,
  onBackPressOnPagesWithSideNav,
  safeAssignFocus,
} from '##/utils';
import { ChildItem, Grid } from '../Grid/Grid';
import { ShelfTitle } from '../Grid/ShelfTitle';
import { VirtualKeyboardRef } from '../VirtualKeyboard/VirtualKeyboardMain';
import { SearchLandingBar } from './SearchLandingBar';

const SEARCH_CONTAINER_NAV_ID = 'search-landing-page-nav';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .search-landing-container {
    width: 100vw;
    height: 100vh;
  }

  .shelf-title {
    margin: pxToVh(47) 0 pxToVh(10);
    padding-left: pxToVw(30);
  }

  .grid-wrapper {
    padding-top: pxToVw(16);
    padding-left: pxToVw(16);
    overflow:hidden;
  }

  .search-page-contents {
    width: 92.8125vw;
    height: 100vh;
    padding: 9vh 0 0 3vw;
    margin-left: pxToVw(68);
  }

  .loading-screen {
    margin-top: pxToVh(33);
    font-size: 2rem;
  }

  .keyboard-container {
    width: 100vw;
    height: 100vh;
    position: relative;
    z-index: 10;
  }
`;

const searchGridLazyLoadBoundary = 2;

export const SearchResults: FunctionalComponent<{
  results: ChildItem[];
  scrollProps: Partial<ScrollProps>;
  searchText: string;
  searchSuccess: boolean;
}> = ({ results, searchText, searchSuccess, scrollProps }) => {
  let title: string;
  title = searchText?.length ? 'Search results' : 'Popular results';
  if (!searchSuccess) {
    title = 'No results found. More for you...';
  }

  return (
    <Fragment>
      <ShelfTitle title={title} className={styles.shelfTitle} />
      <div className={styles.gridWrapper}>
        <Grid
          className={styles.grid}
          items={results}
          gridCols={6}
          id={'search-result-grid'}
          scrollProps={scrollProps}
          lazyLoadBoundary={searchGridLazyLoadBoundary}
          scrollEndBoundary={0}
        />
      </div>
    </Fragment>
  );
};

export const SearchLanding: FunctionalComponent<{}> = () => {
  const [searchTerm, searchTermRef, setSearchTerm] = useReferredState<string>(
    '',
  );
  const [searchResults, setSearchResults] = useState<ChildItem[]>([]);
  const [scrollProps, setScrollProps] = useState<Partial<ScrollProps>>({
    primary: true,
  });
  const [isResultAvailable, setisResultAvailable] = useState<boolean>(true);
  const [isLoadingResults, setIsLoadingResults] = useState(true);
  const { showError } = useErrorScreen();

  const mapSearchResponseToGrid = (
    responseItems: SearchResponse,
  ): ChildItem[] => {
    const fallbackUrl = '/assets/placeholders/Fallback-Poster.jpg';
    return responseItems
      .filter((item) => item.numberOfEpisodes > 0 || item.numberOfExtras > 0)
      .map((item, idx) => ({
        fallbackUrl,
        id: `${item.id}-${idx}`,
        imgHeight: 251,
        imgUrl: item.imageUrl || fallbackUrl,
        imgWidth: 167,
        route: `/show/${item.id}`,
        beforeClickHandler: () => {
          focusCache.search.$set({
            searchTerm: searchTermRef.current,
            lastFocusShowId: item.id,
          });
        },
        showApiEndpoint: item.apiEndpoint,
        subtitle1: `${item.numberOfEpisodes} Episodes`,
        subtitle2: item.subtitle,
      }));
  };

  const getCachedScrollOptions = (
    gridItems: ChildItem[],
  ): Partial<ScrollProps> => {
    let initialScrollIndex = 0;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    let afterInitialScroll = (): void => {};
    if (gridItems.length) {
      const { lastFocusShowId } = focusCache.search.$get();
      if (lastFocusShowId) {
        const matchingResultIdx = gridItems.findIndex((result) =>
          result.id.startsWith(lastFocusShowId),
        );
        if (matchingResultIdx > -1) {
          const prevRow = Math.floor(matchingResultIdx / 6);
          const prevFocusShow = gridItems[matchingResultIdx];
          if (prevRow >= searchGridLazyLoadBoundary) {
            initialScrollIndex = prevRow;
          }
          afterInitialScroll = () => nav.assignFocus(prevFocusShow.id);
        }
      }
      focusCache.search.$clear();
    }
    return {
      initialScrollIndex,
      afterInitialScroll,
    };
  };

  const getSearchResponse = async (term: string): Promise<void> => {
    setIsLoadingResults(true);
    setSearchTerm(term);
    setisResultAvailable(true);

    try {
      let newResults = await tenApiService.collections.getSearchResults(term);
      if (!newResults?.length) {
        newResults = await tenApiService.collections.getSearchResults('');
        setisResultAvailable(false);
      }

      setIsLoadingResults(false);
      const gridItems = mapSearchResponseToGrid(newResults);
      setScrollProps({ primary: true, ...getCachedScrollOptions(gridItems) });
      setSearchResults(gridItems);
    } catch (err) {
      logger.error(err);
      showError(
        (err as RequestRejectionError).internalErrorType || 'NETWORK_ERROR',
      );
      setIsLoadingResults(false);
    }
  };

  const openSearchKeyboard = (): void => {
    ModalLauncher.open({
      component: VirtualKeyboardRef,
      props: {
        defaultFocusId: VirtualKeyBoardNavId,
        navProps: { refocusOnUnmount: false },
        initialText: searchTermRef.current,
        onExitKeyPressed: () => ModalLauncher.close(),
        onSearchTermSelected: getSearchResponse,
      },
    });
  };

  const onComponentMount = async (): Promise<void> => {
    const { searchTerm } = focusCache.search.$get();
    await getSearchResponse(searchTerm || '');
    setLoadingIndicatorVisibility?.(false);
    nav.assignFocus('searchLandingBar');
  };

  useEffect(() => {
    onComponentMount();
  }, []);

  useKeyPress('BACK', (): boolean => {
    const { parent = '' } = nav?.currentFocusNode || {};

    if (ModalLauncher.isModalOpen()) {
      ModalLauncher.close();

      return true;
    }

    if (
      parent !== SIDE_MENU_NAV_ID &&
      parent !== SEARCH_LANDING_BAR_ID &&
      parent !== EXIT_MODAL_NAV_ID
    ) {
      safeAssignFocus(SIDE_MENU_NAV_ID);

      return true;
    }

    return onBackPressOnPagesWithSideNav(parent, SEARCH_LANDING_BAR_ID);
  });

  return (
    <Page isStatic>
      <Nav.Vertical
        id={SEARCH_CONTAINER_NAV_ID}
      >
        <div className={styles.searchPageContents}>
          <SearchLandingBar
            onSelected={() => openSearchKeyboard()}
            searchTerm={searchTerm || 'Search for shows...'}
          />
          {isLoadingResults ? (
            <div className={styles.loadingScreen}>Loading...</div>
          ) : (
            <SearchResults
              results={searchResults}
              scrollProps={scrollProps}
              searchText={searchTerm || ''}
              searchSuccess={isResultAvailable}
            />
          )}
        </div>
      </Nav.Vertical>
    </Page>
  );
};
